'use client';

import * as React from 'react';

type UseRippleOptions = {
  disabled?: boolean;
  centered?: boolean;
};

type UseRippleResult = {
  rippleProps: {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  };
};

const RIPPLE_DURATION_MS = 600;

function spawnRipple(host: HTMLElement, x: number, y: number, timers: Set<number>) {
  if (typeof window === 'undefined') {
    return;
  }
  let container = host.querySelector(':scope > .md-ripple-container');
  if (container === null) {
    const next = document.createElement('span');
    next.className = 'md-ripple-container';
    host.appendChild(next);
    container = next;
  }
  const rect = host.getBoundingClientRect();
  const radius = Math.max(rect.width, rect.height);
  const ripple = document.createElement('span');
  ripple.className = 'md-ripple';
  ripple.style.left = `${x - rect.left - radius}px`;
  ripple.style.top = `${y - rect.top - radius}px`;
  ripple.style.width = `${radius * 2}px`;
  ripple.style.height = `${radius * 2}px`;
  container.appendChild(ripple);
  const timerId = window.setTimeout(() => {
    ripple.remove();
    timers.delete(timerId);
  }, RIPPLE_DURATION_MS);
  timers.add(timerId);
}

/**
 * MD3 ripple effect hook. Returns event handlers to spread on any interactive
 * surface. Spawns a `.md-ripple` span inside a self-managed
 * `.md-ripple-container` (created lazily) so consumers don't need to set it up.
 *
 * The host element must have `overflow: hidden` (or its `.md-ripple-container`
 * descendant clips by default) and a non-static position for the ripple
 * coordinates to anchor correctly.
 */
function useRipple(opts?: UseRippleOptions): UseRippleResult {
  const disabled = opts?.disabled === true;
  const centered = opts?.centered === true;

  const rippleTimersRef = React.useRef<Set<number>>(new Set());

  React.useEffect(() => {
    const timers = rippleTimersRef.current;
    return () => {
      timers.forEach((id) => {
        window.clearTimeout(id);
      });
      timers.clear();
    };
  }, []);

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (disabled === true) {
        return;
      }
      const host = event.currentTarget;
      if (centered === true) {
        const rect = host.getBoundingClientRect();
        spawnRipple(host, rect.left + rect.width / 2, rect.top + rect.height / 2, rippleTimersRef.current);
        return;
      }
      spawnRipple(host, event.clientX, event.clientY, rippleTimersRef.current);
    },
    [disabled, centered],
  );

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (disabled === true) {
        return;
      }
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      const host = event.currentTarget;
      const rect = host.getBoundingClientRect();
      spawnRipple(host, rect.left + rect.width / 2, rect.top + rect.height / 2, rippleTimersRef.current);
    },
    [disabled],
  );

  return {
    rippleProps: {
      onPointerDown,
      onKeyDown,
    },
  };
}

export default useRipple;
export type { UseRippleOptions, UseRippleResult };
